import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyProfile, listMyRequests } from "@/lib/account.functions";
import { listMySpots } from "@/lib/camping.functions";
import { ArrowRight, Wrench, MapPin, User, CheckCircle2, Circle, Plus, Clock } from "lucide-react";

export const Route = createFileRoute("/_authenticated/konto/")({
  component: Overview,
});

function Overview() {
  const getProfile = useServerFn(getMyProfile);
  const getRequests = useServerFn(listMyRequests);
  const getSpots = useServerFn(listMySpots);

  const profile = useQuery({ queryKey: ["my-profile"], queryFn: () => getProfile() });
  const requests = useQuery({ queryKey: ["my-requests"], queryFn: () => getRequests() });
  const spots = useQuery({ queryKey: ["my-spots"], queryFn: () => getSpots() });

  const p = profile.data?.profile;
  const email = profile.data?.email;
  const name = p?.display_name || email?.split("@")[0] || "";

  const fields = [
    { key: "display_name", label: "Namn", value: p?.display_name },
    { key: "phone", label: "Telefon", value: p?.phone },
    { key: "address", label: "Adress", value: p?.address },
    { key: "city", label: "Ort", value: p?.city },
    { key: "avatar_url", label: "Profilbild", value: p?.avatar_url },
    { key: "bio", label: "Om dig", value: p?.bio },
  ];
  const filled = fields.filter((f) => f.value && String(f.value).trim().length > 0).length;
  const pct = Math.round((filled / fields.length) * 100);

  const recentRequests = (requests.data?.requests ?? []).slice(0, 3);
  const recentSpots = (spots.data?.spots ?? []).slice(0, 3);

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="rounded-3xl border border-border bg-card p-6 md:p-8 elev-shadow">
        <p className="eyebrow">Välkommen{name ? `, ${name}` : ""}</p>
        <h2 className="mt-2 font-display text-3xl md:text-4xl">
          Din <span className="font-italic-display text-accent">översikt</span>.
        </h2>

        {/* Profile completion */}
        <div className="mt-6 rounded-2xl border border-border bg-background p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Profil-styrka</p>
              <p className="mt-1 font-display text-2xl">
                {pct}% <span className="text-sm font-normal text-muted-foreground">ifylld</span>
              </p>
            </div>
            <Link
              to="/konto/profil"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition hover:opacity-90"
            >
              Komplettera <ArrowRight size={13} />
            </Link>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-accent transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs">
            {fields.map((f) => {
              const done = f.value && String(f.value).trim().length > 0;
              return (
                <li key={f.key} className={`inline-flex items-center gap-1.5 ${done ? "text-foreground" : "text-muted-foreground"}`}>
                  {done ? <CheckCircle2 size={13} className="text-accent" /> : <Circle size={13} />}
                  {f.label}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          to="/konto/profil"
          icon={<User size={16} />}
          title="Profil"
          value={p?.display_name || "Ej ifylld"}
          hint={email ?? ""}
        />
        <StatCard
          to="/konto/mina-forfragningar"
          icon={<Wrench size={16} />}
          title="Reservdelar"
          value={requests.data?.requests.length ?? 0}
          hint="förfrågningar"
        />
        <StatCard
          to="/konto/mina-tips"
          icon={<MapPin size={16} />}
          title="Campingtips"
          value={spots.data?.spots.length ?? 0}
          hint="publicerade"
        />
      </div>

      {/* Recent requests */}
      <div className="rounded-3xl border border-border bg-card p-6 elev-shadow">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="eyebrow">Senaste</p>
            <h3 className="mt-2 font-display text-2xl">Reservdelsförfrågningar</h3>
          </div>
          <Link to="/reservdelar" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-xs font-medium hover:bg-secondary">
            <Plus size={13} /> Ny förfrågan
          </Link>
        </div>
        {recentRequests.length === 0 ? (
          <p className="mt-5 text-sm text-muted-foreground">
            Du har inte skickat några förfrågningar än.{" "}
            <Link to="/reservdelar" className="text-accent hover:underline">Beställ reservdelar</Link>.
          </p>
        ) : (
          <ul className="mt-5 divide-y divide-border">
            {recentRequests.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {r.brand} {r.model} {r.reg_number ? `· ${r.reg_number}` : ""}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock size={11} />
                    <time dateTime={r.created_at}>
                      {new Date(r.created_at).toLocaleDateString("sv-SE")}
                    </time>
                    <span className="mx-1">·</span>
                    <span className="rounded-full bg-secondary px-2 py-0.5">{r.status}</span>
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
        {recentRequests.length > 0 && (
          <Link to="/konto/mina-forfragningar" className="mt-4 inline-flex items-center gap-1.5 text-sm text-accent hover:underline">
            Se alla <ArrowRight size={14} />
          </Link>
        )}
      </div>

      {/* Recent tips */}
      <div className="rounded-3xl border border-border bg-card p-6 elev-shadow">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="eyebrow">Community</p>
            <h3 className="mt-2 font-display text-2xl">Dina campingtips</h3>
          </div>
          <Link to="/campingtips/ny" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-xs font-medium hover:bg-secondary">
            <Plus size={13} /> Nytt tips
          </Link>
        </div>
        {recentSpots.length === 0 ? (
          <p className="mt-5 text-sm text-muted-foreground">
            Du har inte delat några campingtips än — <Link to="/campingtips/ny" className="text-accent hover:underline">dela din favoritplats</Link>.
          </p>
        ) : (
          <ul className="mt-5 grid gap-3 sm:grid-cols-3">
            {recentSpots.map((s) => (
              <li key={s.id}>
                <Link
                  to="/campingtips/$slug"
                  params={{ slug: s.slug }}
                  className="group block overflow-hidden rounded-2xl border border-border hover-lift"
                >
                  <div className="aspect-[4/3] bg-secondary">
                    {s.image_urls?.[0] ? (
                      <img src={s.image_urls[0]} alt={s.title} loading="lazy" className="h-full w-full object-cover transition group-hover:scale-105" />
                    ) : null}
                  </div>
                  <div className="p-3">
                    <p className="truncate text-sm font-medium">{s.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{s.region || s.spot_type}</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatCard({
  to, icon, title, value, hint,
}: {
  to: string; icon: React.ReactNode; title: string; value: string | number; hint: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-start justify-between gap-4 rounded-3xl border border-border bg-card p-5 transition hover:border-accent/60 elev-shadow"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-accent/10 text-accent">{icon}</span>
          {title}
        </div>
        <p className="mt-3 truncate font-display text-2xl">{value}</p>
        <p className="mt-1 truncate text-xs text-muted-foreground">{hint}</p>
      </div>
      <ArrowRight size={16} className="mt-1 shrink-0 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-accent" />
    </Link>
  );
}
